/**
 * Eufemia Docs MCP — Cloudflare Worker entry.
 *
 * Hosts the Eufemia documentation MCP tools on Cloudflare Workers using the
 * MCP SDK's web-standard Streamable HTTP transport.
 *
 * Endpoints:
 * - `GET  /healthz` — health check.
 * - `POST /mcp`     — Streamable HTTP transport (initialize and subsequent messages).
 * - `GET  /mcp`     — SSE stream for server-to-client notifications.
 * - `DELETE /mcp`   — terminate a session.
 *
 * Auth (optional): set the `MCP_AUTH_TOKEN` Worker secret. When set, every
 * request to `/mcp` must send `Authorization: Bearer <token>`.
 *
 * The docs corpus is shipped inside the Worker bundle as `docs.bundle.json`,
 * generated by `tools/eufemia-llm-metadata/scripts/buildDocsBundle.ts`.
 */

import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js'
import { WebStandardStreamableHTTPServerTransport } from '@modelcontextprotocol/sdk/server/webStandardStreamableHttp.js'

import {
  SERVER_INFO,
  createDocsTools,
  registerDocsTools,
  validateDocsSource,
} from '../mcp-docs-server'
import { createBundledDocsSource } from '../docs-source'

// Wrangler bundles JSON imports as plain objects in the script.
import docsBundle from './docs.bundle.json'

export type Env = {
  MCP_AUTH_TOKEN?: string
}

// Create the bundled docs source once at module load. It is purely
// in-memory, so the same instance can serve every request the Worker handles.
const docsSource = createBundledDocsSource(
  docsBundle as Record<string, string>,
  { label: 'worker:docs.bundle.json' }
)

let validatedOnce = false

async function ensureValidated() {
  if (!validatedOnce) {
    await validateDocsSource(docsSource)
    validatedOnce = true
  }
}

function authResponse(): Response {
  return new Response(
    JSON.stringify({
      jsonrpc: '2.0',
      error: { code: -32001, message: 'Unauthorized' },
      id: null,
    }),
    {
      status: 401,
      headers: {
        'content-type': 'application/json',
        'www-authenticate': 'Bearer realm="eufemia-mcp"',
      },
    }
  )
}

/**
 * Constant-time string comparison using the Web Crypto API. Works in
 * Cloudflare Workers where `node:crypto.timingSafeEqual` is unavailable.
 */
async function safeEqual(a: string, b: string): Promise<boolean> {
  const encoder = new TextEncoder()
  const keyData = encoder.encode(a)
  const key = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign', 'verify']
  )
  const sig = await crypto.subtle.sign('HMAC', key, encoder.encode(a))
  return crypto.subtle.verify('HMAC', key, sig, encoder.encode(b))
}

async function checkAuth(
  request: Request,
  env: Env
): Promise<Response | null> {
  const token = env.MCP_AUTH_TOKEN
  if (!token) {
    return null
  }
  const header = request.headers.get('authorization') ?? ''
  const expected = `Bearer ${token}`
  if (
    header.length === expected.length &&
    (await safeEqual(header, expected))
  ) {
    return null
  }
  return authResponse()
}

async function handleMcp(request: Request, env: Env): Promise<Response> {
  const unauthorized = await checkAuth(request, env)
  if (unauthorized) {
    return unauthorized
  }

  const tools = createDocsTools({ source: docsSource })
  const server = new McpServer(SERVER_INFO)
  registerDocsTools(server, tools)

  const transport = new WebStandardStreamableHTTPServerTransport({
    // Stateless: a fresh transport per request keeps the Worker simple and
    // matches Workers' short-lived isolate model. Streamable HTTP supports
    // this mode for one-shot calls; clients that need cross-request state can
    // be added later with Durable Objects.
    sessionIdGenerator: undefined,
    enableJsonResponse: true,
  })

  await server.connect(transport)

  try {
    return await transport.handleRequest(request)
  } finally {
    // Free per-request resources eagerly. The transport is single-use here.
    try {
      await transport.close()
    } catch {
      // ignore
    }
  }
}

function handleHealth(): Response {
  return new Response(
    JSON.stringify({
      ok: true,
      name: SERVER_INFO.name,
      version: SERVER_INFO.version,
      transports: ['streamable-http'],
      runtime: 'cloudflare-worker',
    }),
    {
      status: 200,
      headers: { 'content-type': 'application/json' },
    }
  )
}

const handler = {
  async fetch(request: Request, env: Env): Promise<Response> {
    const url = new URL(request.url)

    if (url.pathname === '/healthz' && request.method === 'GET') {
      return handleHealth()
    }

    if (url.pathname === '/mcp') {
      try {
        await ensureValidated()
      } catch (e) {
        return new Response(
          `Eufemia MCP misconfigured: ${(e as Error).message}`,
          { status: 500 }
        )
      }

      if (
        request.method !== 'POST' &&
        request.method !== 'GET' &&
        request.method !== 'DELETE'
      ) {
        return new Response('Method Not Allowed', { status: 405 })
      }
      return handleMcp(request, env)
    }

    return new Response('Not Found', { status: 404 })
  },
}

export default handler
